import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { deselectEvent } from "app/actions/events";
import { CalendarViewType, VIEW_LABELS } from "app/models/CalendarPreferences";
import ResourceIcon from "app/components/ResourceIcon";
import { storeResourceView } from "app/routes/FacilityCalendarPage/facilityView";
import { cls } from "app/utils";

import styles from "./styles.module.scss";

const VIEW_OPTIONS: CalendarViewType[] = [
  "timeGridDay",
  "timeGridWeek",
  "dayGridMonth",
  "listWeek",
];

const VIEW_ICONS: Record<CalendarViewType, string> = {
  timeGridDay: "ico-cal-day",
  timeGridWeek: "ico-cal-week",
  dayGridMonth: "ico-cal-month",
  listWeek: "ico-cal-schedule",
};

type PropsDef = {
  currentView: CalendarViewType;
  onViewChange: (view: CalendarViewType) => void;
  showFacilityCalendar?: boolean;
  isFacilityCalendarView?: boolean;
  // Adds "Resource Calendar" under "Facility Calendar". Shown while the
  // facility has `resource_management` on, for any coach count.
  showResourceCalendar?: boolean;
  // True while the facility calendar shows the bay columns.
  isResourceCalendarView?: boolean;
  // Set on the facility calendar page, which switches its columns in place.
  // Without it, a facility entry stores the choice and opens that page.
  onFacilityViewChange?: (resourceView: boolean) => void;
  // True for an admin and a scheduler. Shows only the two facility entries,
  // and hides the time-grid and list views.
  facilityViewsOnly?: boolean;
};

export default function ViewSelector({
  currentView,
  onViewChange,
  showFacilityCalendar,
  isFacilityCalendarView,
  showResourceCalendar = false,
  isResourceCalendarView = false,
  onFacilityViewChange,
  facilityViewsOnly = false,
}: PropsDef) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  // The event popup points at a card of the current view. A view change
  // draws new cards, and the facility entries open another page that reads
  // the same selected event, so the popup closes on each view change.
  const closeEventPopup = () => {
    dispatch(deselectEvent());
  };

  const handleSelect = (view: CalendarViewType) => {
    closeEventPopup();
    onViewChange(view);
    setIsOpen(false);
  };

  // Opens one column type of the facility calendar. With the flag off, the
  // entry opens the facility page as before and stores nothing.
  const selectFacilityView = (resourceView: boolean) => {
    setIsOpen(false);
    closeEventPopup();
    if (onFacilityViewChange) {
      onFacilityViewChange(resourceView);
      return;
    }
    if (showResourceCalendar) {
      storeResourceView(resourceView);
    }
    history.push("/calendar/facility");
  };

  const resourceViewActive = Boolean(
    isFacilityCalendarView && isResourceCalendarView,
  );

  let buttonLabel = VIEW_LABELS[currentView];
  if (isFacilityCalendarView) {
    buttonLabel = resourceViewActive ? "Resource Calendar" : "Facility Calendar";
  }

  return (
    <div className={styles.view_selector} ref={containerRef}>
      <button
        type="button"
        className={styles.view_selector_button}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="view-selector-button"
      >
        {resourceViewActive ? (
          <span className={cls(styles.calendar_icon, styles.svg_icon)}>
            <ResourceIcon />
          </span>
        ) : (
          <i
            className={cls(
              "ico",
              isFacilityCalendarView
                ? "ico-grouped-people-outline"
                : VIEW_ICONS[currentView],
              styles.calendar_icon,
            )}
          />
        )}
        <span className={styles.view_label}>{t(buttonLabel)}</span>
        <i
          className={cls("ico ico-chevron", styles.chevron_icon)}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>

      {isOpen && (
        <div className={styles.dropdown_menu} data-testid="view-menu">
          {!facilityViewsOnly &&
            VIEW_OPTIONS.map((view) => (
              <button
                key={view}
                type="button"
                className={cls(
                  styles.dropdown_item,
                  !isFacilityCalendarView &&
                    view === currentView &&
                    styles.dropdown_item_active,
                )}
                onClick={() => handleSelect(view)}
              >
                <i
                  className={cls("ico", VIEW_ICONS[view], styles.dropdown_icon)}
                />
                {t(VIEW_LABELS[view])}
              </button>
            ))}
          {(showFacilityCalendar || showResourceCalendar) && (
            <>
              {!facilityViewsOnly && (
                <hr className={styles.dropdown_separator} />
              )}
              {showFacilityCalendar && (
                <button
                  type="button"
                  className={cls(
                    styles.dropdown_item,
                    isFacilityCalendarView &&
                      !resourceViewActive &&
                      styles.dropdown_item_active,
                  )}
                  onClick={() => selectFacilityView(false)}
                >
                  <i
                    className={cls(
                      "ico",
                      "ico-grouped-people-outline",
                      styles.dropdown_icon,
                    )}
                  />
                  {t("Facility Calendar")}
                </button>
              )}
              {showResourceCalendar && (
                <button
                  type="button"
                  className={cls(
                    styles.dropdown_item,
                    resourceViewActive && styles.dropdown_item_active,
                  )}
                  onClick={() => selectFacilityView(true)}
                >
                  <span className={cls(styles.dropdown_icon, styles.svg_icon)}>
                    <ResourceIcon />
                  </span>
                  {t("Resource Calendar")}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
